import React from 'react';

import { Periodicity } from '@components/time-period-selector/PeriodsTypes';

import { useAnalytics } from '@ecosystem/newron-client-lib';
import { useSelector } from 'react-redux';
import { NoResult } from '@pages/NoResult';
import { AVERAGE_SELLING_PRICE } from '@pages/Utilities';
import { useInViewTracking } from '@utils/hook/tracking/UseInViewTracking';
import { ElementType } from '@utils/page-analytics/model/AnalyticTrackingBasePropertiesObj';
import {
  PAGE_ELEMENT_VIEW_TRACKING_TITLE,
  PageElementViewTrackingParam,
} from '@utils/page-analytics/model/PageElementViewedTracking';
import { FactRevenue, PageName, PageType } from '@utils/pageConfig/Types';
import { renderVisualizationDefer } from '../utilities/ChartUtils';
import { AveragePriceChartResp } from './Types';
import { usePageConfig } from '@utils/pageConfig/PagesConfigContext';
import { getInitialAppliedCountries } from '@utils/FilterUtils';
import { ProductType } from '@pages/product-analyzer/Product';
import {
  createChartAxisConfig,
  processOverviewLineChartData,
} from '@data-visualization/utilities/line-chart/FormatLineChartData';
import { WithLineChart } from '@components/design';
import { useExportImages } from '@hooks/export/UseExportChartImage';
import { ChartKeys } from '@const/ChartKeys';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';

const useAveragePriceChartCreateViewTrackingParam = (data: AveragePriceChartResp) => {
  const { track } = useAnalytics();
  const pagesConfig = usePageConfig();
  const sideFiltersState = useSelector((state: any) => state.sideFilters);
  const retailerClientState = useSelector((state: any) => state.retailerClient);
  const mainFiltersStateRO = useSelector((state: any) => state.mainFiltersRO);

  const createParam: () => PageElementViewTrackingParam = () => {
    return {
      eventTitle: PAGE_ELEMENT_VIEW_TRACKING_TITLE,
      sideFilters: sideFiltersState.appliedFilters,
      track: track,
      retailerClientState: retailerClientState,
      pageType: PageType.RETAILER_OVERVIEW,
      pageName: PageName.RETAILER_OVERVIEW,
      componentName: AVERAGE_SELLING_PRICE,
      elementType: ElementType.LINE_CHART,
      mainFilterState: mainFiltersStateRO,
      hasData: !!data,
      access: pagesConfig.access,
      initialLocationId: getInitialAppliedCountries(
        pagesConfig,
        PageType.RETAILER_OVERVIEW,
        ProductType.PerformanceAnalyzer,
      ),
    };
  };
  return { createParam };
};

interface Props {
  isLoading: boolean;
  data: AveragePriceChartResp;
  selectedPeriodicity: Periodicity;
  factRevenue: FactRevenue;
  isUsingCollection?: boolean;
}

const AverageSellingPriceChart: React.FC<Props> = ({
  isLoading,
  data,
  selectedPeriodicity,
  factRevenue,
  isUsingCollection,
}) => {
  const { createParam } = useAveragePriceChartCreateViewTrackingParam(data);

  const { ref: RetailerOverviewAveragePriceCardComponent } = useInViewTracking(createParam);

  const { handleExportImage } = useExportImages(PageType.RETAILER_OVERVIEW);

  const getChartName = ChartKeys[PageType.RETAILER_OVERVIEW].AVG_SELLING_PRICE;

  const axesConfig = createChartAxisConfig[PageType.RETAILER_OVERVIEW][getChartName](selectedPeriodicity, factRevenue);
  const jsxToRenderDefer = () => {
    return (
      <div ref={isUsingCollection ? null : RetailerOverviewAveragePriceCardComponent}>
        <div className="w-full" style={{ minWidth: '100%' }}>
          <div data-testid="avg-selling-price-chart">
            <WithLineChart
              data={processOverviewLineChartData(data)}
              onExport={(image) => handleExportImage(image, generateUniqueChartKeys(data))}
              {...axesConfig}
            ></WithLineChart>
          </div>
        </div>
      </div>
    );
  };

  const noResultJsxDefer = () => {
    return <div ref={isUsingCollection ? null : RetailerOverviewAveragePriceCardComponent}>{NoResult()}</div>;
  };

  return renderVisualizationDefer({
    isLoading: isLoading,
    fetchedApiData: data,
    defaultData: new AveragePriceChartResp(),
    jsxToRenderDefer,
    noResultJsxDefer,
  });
};

export default AverageSellingPriceChart;
